import { useLayoutEffect, useRef, type TouchEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTabNav } from '../components/layout/TabNav';
import { sensX } from '../i18n';

/**
 * Largeur de la bande sensible, au bord gauche.
 *
 * Le geste ne part que de la : ailleurs, il volerait tout balayage
 * horizontal a venir dans le contenu, et surtout il partirait pour un rien
 * pendant qu'on fait defiler.
 */
const EDGE_PX = 30;

/** Course minimale avant de decider que le geste est horizontal. */
const ENGAGE_PX = 10;

/** Au-dela de cette part de l'ecran, lacher revient en arriere. */
const COMMIT_RATIO = 0.3;

/** Duree du rattrapage apres le lacher, en millisecondes. */
const SETTLE_MS = 260;

/**
 * Revenir en arriere en tirant depuis le bord gauche.
 *
 * L'ecran suit le doigt et sort par la droite — le chemin inverse de
 * celui par lequel il est entre. Sous un tiers de l'ecran, lacher le
 * remet en place : un geste qu'on ne peut pas annuler se termine toujours
 * par une erreur.
 *
 * L'ecran precedent est pose derriere pendant le geste, et n'accepte
 * aucun appui tant qu'on n'y est pas tout a fait : on regarde ou l'on va
 * avant d'y etre.
 *
 * L'ecran est deplace directement dans le DOM, sans passer par un etat
 * React : un fil de discussion se rerendrait soixante fois par seconde
 * pour une seule `transform`. Seule la presence de l'ecran de derriere
 * passe par React, et elle ne change que deux fois par geste.
 */
export function useSwipeBack(onBack?: () => void) {
  const navigate = useNavigate();
  const { backPath, backPeek, showBack } = useTabNav();

  const host = useRef<HTMLDivElement>(null);
  /** La bande commune aux deux ecrans : c'est elle qui porte l'avancement. */
  const strip = useRef<HTMLElement | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);
  const engaged = useRef(false);
  const offset = useRef(0);
  /** La zone defilante figee pendant le geste, et sa position. */
  const frozen = useRef<{ el: HTMLElement; top: number } | null>(null);

  function onTouchStart(event: TouchEvent<HTMLDivElement>) {
    const touch = event.touches[0];
    const node = host.current;
    // Toujours oublier le geste precedent, meme si celui-ci ne part pas du
    // bord : un `touchend` manque laisserait sinon une origine perimee.
    start.current = null;
    if (!touch || !node) return;

    // Le bord gauche de l'ecran, pas celui de l'element : ils coincident
    // ici, mais c'est la position du doigt sur la vitre qui compte.
    // Le bord d'ou l'on revient : a gauche, ou a droite quand on lit l'arabe.
    if ((sensX === 1 ? touch.clientX : window.innerWidth - touch.clientX) > EDGE_PX) return;

    start.current = { x: touch.clientX, y: touch.clientY };
    engaged.current = false;
    offset.current = 0;

    /*
      L'ecran de derriere est monte des que le doigt se pose, pas au
      moment ou le geste s'engage.

      Il l'etait apres dix pixels de course — mais l'ecran qu'on quitte,
      lui, suit le doigt des le premier pixel. Entre les deux, React
      montait la route precedente : le temps d'un rendu complet sur un
      telephone, on tirait un ecran sur du vide. Un trou noir de deux
      dixiemes de seconde au depart de chaque retour, qu'on prend pour
      une panne — d'autant qu'un geste abandonne avant le tiers de
      l'ecran ne montre jamais que ce trou.

      Le poser ici donne a React toute la course d'engagement pour
      monter, et cela ne coute rien a voir : a `--back: 0` la bande de
      dessous est decalee de trente pour cent vers la gauche, entierement
      cachee par l'ecran courant.

      Un doigt qui se pose au bord sans rien tirer le retire aussitot :
      voir `oublierLePeek`.

      Et tant qu'il n'a rien tire, l'ecran courant doit passer par-dessus
      la barre d'onglets que ce montage vient d'allumer — sans quoi elle
      se pose en travers de l'ecran qu'on regarde. Voir
      `screen-back--pret`.
    */
    if (!backPath) return;
    showBack(backPath);
    node.classList.add('screen-back--pret');
  }

  /**
   * Retirer l'ecran de derriere quand le geste n'aura pas lieu.
   *
   * Le bord gauche sert aussi a toucher ce qui s'y trouve, et a faire
   * defiler : monter la route precedente a chaque effleurement serait
   * sans consequence visible, mais la laisser montee garderait un ecran
   * entier en vie pour rien — et `TabSwipe` refuse le balayage d'onglets
   * tant qu'un peek existe.
   */
  function oublierLePeek() {
    if (engaged.current) return;
    showBack(null);
    /* La marque ne tombe pas ici : voir l'effet qui suit. */
  }

  /*
    L'ecran courant ne repasse sous la barre qu'une fois la barre partie.

    Retirer le peek se fait en deux temps qui ne tombent pas ensemble :
    `showBack(null)` attend le prochain rendu de React — qui doit demonter
    un ecran entier, celui de derriere, avec sa barre d'onglets —, alors
    que la marque `screen-back--pret` tombait sur-le-champ. Entre les
    deux, l'ecran courant perdait sa place au-dessus d'une barre encore
    montee, et elle se posait en travers : cinq onglets par-dessus
    « Ajouter des amis » pour un doigt pose au bord qui descendait au
    lieu de glisser. Le temps d'un demontage sur un telephone, c'est
    parfois une demi-seconde.

    La marque tombe donc dans le rendu meme ou le peek disparait, avant
    que le navigateur ne peigne : la barre et la marque s'en vont
    ensemble.
  */
  useLayoutEffect(() => {
    if (backPeek === null && !engaged.current) {
      host.current?.classList.remove('screen-back--pret');
    }
  }, [backPeek]);

  function onTouchMove(event: TouchEvent<HTMLDivElement>) {
    const from = start.current;
    const touch = event.touches[0];
    const node = host.current;
    if (!from || !touch || !node) return;

    /* Mesure dans le sens de lecture : en arabe, on revient vers la gauche. */
    const dx = (touch.clientX - from.x) * sensX;
    const dy = touch.clientY - from.y;

    if (!engaged.current) {
      // A rebours ou plutot vertical : ce n'est pas un retour.
      if (dx <= 0 || Math.abs(dy) > Math.abs(dx)) {
        start.current = null;
        oublierLePeek();
        return;
      }
      if (dx < ENGAGE_PX) return;

      engaged.current = true;
      /*
        Le clavier descend des que le geste s'engage.

        Il ne peut pas suivre l'ecran vers la droite : depuis iOS 26, le
        systeme le dessine hors de l'application, et rien ne permet de le
        deplacer — essaye avec un greffon natif, le 14 octobre 2026. Il
        restait donc fige pendant que la conversation partait. Le fermer
        le fait descendre pendant qu'elle glisse, comme un ecran qu'on
        quitte. Un geste abandonne le laisse ferme : on le rouvre d'une
        touche.
      */
      const actif = document.activeElement;
      if (actif instanceof HTMLElement && actif.matches('input, textarea, [contenteditable="true"]')) {
        actif.blur();
      }
      /*
        L'animation d'entree ne doit plus exister a partir d'ici.

        Elle est neutralisee pendant le geste par un `animation: none`
        porte par les classes du balayage — mais une animation CSS
        redemarre des qu'on lui rend le droit d'exister. Les classes
        tombant a la fin du repli, l'ecran refaisait son entree par la
        droite au moment precis ou il revenait en plein ecran : c'est ce
        qu'on prenait pour une reinitialisation.

        La retirer ici ne coute rien : elle dure moins d'une demi-seconde
        et s'est achevee bien avant qu'un doigt ne se pose.
      */
      node.classList.remove(
        'sub-screen--enter',
        'sub-screen--zoom',
        'ms-thread--enter',
        'screen-back--pret',
      );
      node.classList.add('screen-back--held');
      strip.current = node.closest('.tab-swipe');
      freezeScroll(node);
      /* L'ecran de derriere est deja monte depuis `onTouchStart` : il a eu
         toute la course d'engagement pour se rendre. On le redemande quand
         meme, au cas ou un geste precedent l'aurait retire. */
      if (backPath) showBack(backPath);
    }

    offset.current = Math.max(0, dx);
    node.style.transform = `translate3d(${offset.current * sensX}px, 0, 0)`;
    strip.current?.style.setProperty('--back', String(offset.current / node.clientWidth));
  }

  function onTouchEnd() {
    start.current = null;
    const node = host.current;
    if (!engaged.current || !node) {
      oublierLePeek();
      return;
    }

    engaged.current = false;
    thawScroll();

    const commit = offset.current > node.clientWidth * COMMIT_RATIO;
    node.classList.remove('screen-back--held');
    node.classList.add('screen-back--settling');
    node.style.transform = commit ? `translate3d(${100 * sensX}%, 0, 0)` : '';
    strip.current?.classList.add('tab-swipe--settling');
    strip.current?.style.setProperty('--back', commit ? '1' : '0');

    if (!commit) {
      // Les classes doivent tomber une fois la transition finie, sinon le
      // prochain geste demarrerait en glissant au lieu de suivre le doigt.
      window.setTimeout(() => {
        node.classList.remove('screen-back--settling');
        strip.current?.classList.remove('tab-swipe--settling');
        strip.current = null;
        showBack(null);
      }, SETTLE_MS);
      return;
    }

    /*
      On navigue et l'on retire le peek dans la meme passe : la bande de
      derriere porte deja l'adresse d'arrivee, et comme les bandes sont
      identifiees par leur adresse, c'est elle qui reste — sans se
      remonter, donc sans relire ses donnees.
    */
    window.setTimeout(() => {
      showBack(null);
      if (onBack) onBack();
      else navigate(-1);

      /*
        Et l'ecran repousse retrouve sa place.

        On le laissait a `translate3d(100%)` en comptant sur son
        remplacement : la bande porte l'adresse pour cle, elle change donc
        au retour et le noeud est jete. Sauf quand l'adresse ne change
        pas — ce qui arrive apres une navigation `replace`, ou revenir
        ramene la ou l'on est deja. Le meme noeud reste alors en place avec
        sa translation, hors de l'ecran : plus rien a voir, un fond uni,
        et rien pour en sortir.

        Le remettre a zero ne coute rien quand il est jete, et sauve
        l'ecran quand il ne l'est pas.
      */
      node.style.transform = '';
      node.classList.remove('screen-back--settling');
      strip.current?.classList.remove('tab-swipe--settling');
      strip.current?.style.removeProperty('--back');
      strip.current = null;
    }, SETTLE_MS);
  }

  /*
    Pendant le geste, le contenu ne defile plus : le navigateur garde la
    main sur le vertical — c'est ce qui permet de detecter le geste sans
    bloquer la lecture — et l'ecran serait parti en diagonale.
  */
  function freezeScroll(node: HTMLElement) {
    const el = node.querySelector<HTMLElement>('.scroll-area');
    if (!el) return;

    frozen.current = { el, top: el.scrollTop };
    el.style.overflowY = 'hidden';
    el.scrollTop = frozen.current.top;
  }

  function thawScroll() {
    const held = frozen.current;
    frozen.current = null;
    if (!held) return;

    held.el.style.overflowY = '';
    held.el.scrollTop = held.top;
  }

  return { ref: host, onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: onTouchEnd };
}
